// @mui material components
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import Divider from "@mui/material/Divider";
import MDTypography from "components/MDTypography";
import { useState, useEffect, useMemo } from "react";
import { useSnackbar } from "components/GlobalSnackbar";
import { handleApiError } from "utils/errorHandler";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Dashboard components
import Projects from "./dashboard/components/Projects";
import JanaTerakhir from "./dashboard/components/JanaTerakhir";

// Material UI Controller for user context
import { useMaterialUIController } from "context";

// Prisma queries

import {
  apiGetDashboardStats,
  apiGetMonthlyArrearsData,
  apiGetUEUActivePremisCount,
  apiGetOpenInboisPelbagaiTotal,
  apiGetPenyewaCounts,
  apiGetTerimaanSummary,
  apiGetMonthlyTerimaanData,
} from "api/dashboard";
import { apiGetTunggakanSummary, apiGetTunggakanPerZon } from "api/tunggakan";

function Dashboard() {
  // Get user from context
  const [controller] = useMaterialUIController();
  const { user } = controller;
  
  // Determine dashboard type based on user
  const isKaunterUser = user?.menu?.toUpperCase() === "KAUNTER" || user?.id_pengguna?.toLowerCase() === "huda";
  const dashboardTitle = isKaunterUser ? "Dashboard KEW" : "Dashboard UEU";

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    properties: { total: 0, active: 0, inactive: 0 },
    invoices: { total: 0, paid: 0, unpaid: 0 },
    financial: { totalOutstanding: 0, totalPayments: 0 },
  });
  const [tunggakanSummary, setTunggakanSummary] = useState({ totalOutstanding: 0 });
  const [monthlyArrearsData, setMonthlyArrearsData] = useState([]);
  const [zonArrearsData, setZonArrearsData] = useState([]);
  const [zonArrearsTotal, setZonArrearsTotal] = useState(0);
  const [asAtLabel, setAsAtLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [ueuActivePremisCount, setUEUActivePremisCount] = useState(null);
  const [openInboisPelbagaiTotal, setOpenInboisPelbagaiTotal] = useState(0);
  const [penyewaCounts, setPenyewaCounts] = useState({ total: 0, active: 0 });
  const { showError, showSuccess } = useSnackbar();
  // Track when dashboard data was last updated and keep a minute ticker for live label
  const [lastUpdated, setLastUpdated] = useState(null);
  const [minuteTick, setMinuteTick] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch dashboard data function (reusable for refresh)
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const todayStr = `${yyyy}-${mm}-${dd}`; // YYYY-MM-DD
        
        // Fetch different data based on user type
        if (isKaunterUser) {
          // KEW Dashboard - Terimaan data
          const [stats, monthlyTerimaan, ueuCount, terimaanSum, perZon, openPelbagai, penyewa] = await Promise.all([
            apiGetDashboardStats(),
            apiGetMonthlyTerimaanData(),
            apiGetUEUActivePremisCount(),
            apiGetTerimaanSummary(),
            apiGetTunggakanPerZon(null, todayStr), // Keep for PERHATIAN card
            apiGetOpenInboisPelbagaiTotal(),
            apiGetPenyewaCounts(),
          ]);
          console.log('[KEW Dashboard] Monthly Terimaan Data:', monthlyTerimaan);
          setDashboardData(stats);
          setMonthlyArrearsData(monthlyTerimaan);
          setUEUActivePremisCount(ueuCount);
          setTunggakanSummary(terimaanSum); // Contains totalOutstanding for terimaan
          setZonArrearsData(perZon?.data || []);
          const totalFromApi = typeof perZon?.total === "number" ? perZon.total : 0;
          const totalFromData = (perZon?.data || []).reduce((s, r) => s + (Number(r.jumlah) || 0), 0);
          setZonArrearsTotal(totalFromApi || totalFromData);
          setOpenInboisPelbagaiTotal(openPelbagai || 0);
          setPenyewaCounts(penyewa || { total: 0, active: 0 });
          setAsAtLabel(new Date(todayStr).toLocaleDateString("en-GB"));
        } else {
          // UEU Dashboard - Tunggakan data (existing logic)
          const [stats, monthlyData, ueuCount, tunggakan, perZon, openPelbagai, penyewa] = await Promise.all([
            apiGetDashboardStats(),
            apiGetMonthlyArrearsData(),
            apiGetUEUActivePremisCount(),
            apiGetTunggakanSummary(),
            apiGetTunggakanPerZon(null, todayStr),
            apiGetOpenInboisPelbagaiTotal(),
            apiGetPenyewaCounts(),
          ]);
          console.log('[UEU Dashboard] Monthly Arrears Data:', monthlyData);
          setDashboardData(stats);
          setMonthlyArrearsData(monthlyData);
          setUEUActivePremisCount(ueuCount);
          setTunggakanSummary(tunggakan);
          setZonArrearsData(perZon?.data || []);
          const totalFromApi = typeof perZon?.total === "number" ? perZon.total : 0;
          const totalFromData = (perZon?.data || []).reduce((s, r) => s + (Number(r.jumlah) || 0), 0);
          setZonArrearsTotal(totalFromApi || totalFromData);
          setOpenInboisPelbagaiTotal(openPelbagai || 0);
          setPenyewaCounts(penyewa || { total: 0, active: 0 });
          setAsAtLabel(new Date(todayStr).toLocaleDateString("en-GB"));
        }
      } catch (error) {
        handleApiError(error, showError);
        // Keep default values if there's an error
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  };

  // Fetch dashboard data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []); // Empty dependency array - only run once on mount

  // Bump a minute ticker so the "updated … ago" label refreshes automatically
  useEffect(() => {
    const id = setInterval(() => setMinuteTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Manual refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchDashboardData();
    setIsRefreshing(false);
    showSuccess("Dashboard dikemaskini");
  };

  // Build fiscal year labels (Jan-Dec of current year) in MM/YY format to match backend data
  const rollingMonthYearLabels = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const yearShort = String(currentYear).slice(-2); // Get last 2 digits of year
    const labels = [];
    // Generate labels for Jan through Dec of current year in MM/YY format
    for (let month = 1; month <= 12; month++) {
      const monthStr = String(month).padStart(2, "0");
      labels.push(`${monthStr}/${yearShort}`);
    }
    return labels;
  }, []);

  // Prepare chart data with real monthly arrears - memoized to prevent infinite re-renders
  const dynamicBarChartData = useMemo(() => {
    // Build per-zon horizontal bar chart (zon on y-axis, amount on x-axis)
    const labels = (zonArrearsData || []).map((z) => {
      const n = ((z && z.zon) || "").toString().trim();
      if (!n) return "TIDAK DIKETAHUI";
      return n.toLowerCase() === "tidak diketahui" ? "TIDAK DIKETAHUI" : n;
    });
    const data = (zonArrearsData || []).map((z) => Number(z.jumlah || 0));
    return {
      labels,
      datasets: [
        {
          label: "Tunggakan (RM)",
          color: "success",
          data,
        },
      ],
    };
  }, [zonArrearsData]);

  // Dynamically adjust chart height so all zon labels are visible
  const chartHeight = useMemo(() => {
    const count = zonArrearsData?.length || 0;
    // ~36px per bar + some padding, with a sensible minimum
    return Math.max(300, count * 36);
  }, [zonArrearsData]);

  // Calculate tunggakan percentage change - memoized for performance
  const tunggakanPercentageChange = useMemo(() => {
    if (monthlyArrearsData.length < 2) {
      return { percentage: 0, isIncrease: false };
    }

    // Get the latest month (current month) and previous month values
    const currentMonth = monthlyArrearsData[monthlyArrearsData.length - 1] || 0;
    const previousMonth = monthlyArrearsData[monthlyArrearsData.length - 2] || 0;

    // Handle edge cases
    if (previousMonth === 0) {
      if (currentMonth > 0) {
        return { percentage: 100, isIncrease: true }; // 100% increase from 0
      }
      return { percentage: 0, isIncrease: false };
    }

    // Calculate percentage change: ((current - previous) / previous) * 100
    const percentageChange = ((currentMonth - previousMonth) / previousMonth) * 100;
    const isIncrease = percentageChange >= 0;

    return {
      percentage: Math.abs(Math.round(percentageChange * 10) / 10), // Round to 1 decimal place
      isIncrease: isIncrease,
    };
  }, [monthlyArrearsData]);

  // Generate dynamic description based on real data
  const tunggakanDescription = useMemo(() => {
    if (loading || monthlyArrearsData.length < 2) {
      return "Mengira perubahan tunggakan...";
    }

    const { percentage, isIncrease } = tunggakanPercentageChange;
    const direction = isIncrease ? "kenaikan" : "penurunan";
    const sign = isIncrease ? "+" : "-";

    return `(${sign}${percentage}%) ${direction} tunggakan bulan ini.`;
  }, [loading, monthlyArrearsData, tunggakanPercentageChange]);

  // Compute live “updated … ago” label for the bar chart
  const updatedAgoLabel = useMemo(() => {
    if (!lastUpdated) return "memuatkan...";
    const diffMs = Date.now() - lastUpdated.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin <= 0) return "updated just now";
    return `updated ${diffMin} min ago`;
  }, [lastUpdated, minuteTick]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={2}>
        {/* Header with refresh button */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3} px={1}>
          <MDBox>
            <MDTypography variant="h5" fontWeight="medium">
              {dashboardTitle}
            </MDTypography>
            <MDTypography variant="caption" color="text" fontSize="0.75rem">
              {lastUpdated ? updatedAgoLabel : "Loading..."}
            </MDTypography>
          </MDBox>
          <IconButton 
            onClick={handleRefresh} 
            disabled={isRefreshing || loading}
            size="small"
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: 'grey.300' }
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </MDBox>

        <Grid container spacing={2}>
          {/* LEFT COLUMN: Tunggakan Card + Notis Summary */}
          <Grid item xs={12} lg={4}>
            <Grid container spacing={2}>
              {/* PRIMARY METRIC: Tunggakan or Terimaan based on user */}
              <Grid item xs={12}>
                <MDBox mb={1}>
                  <ComplexStatisticsCard
                    color="primary"
                    icon={isKaunterUser ? "payments" : "money_off"}
                    title={isKaunterUser ? "Jumlah Terimaan" : "Jumlah Tunggakan"}
                    count={
                      loading
                        ? "..."
                        : `RM ${tunggakanSummary.totalOutstanding.toLocaleString()}`
                    }
                    percentage={{
                      color: isKaunterUser 
                        ? (tunggakanPercentageChange.percentage === 0 ? "error" : "success")
                        : (tunggakanPercentageChange.isIncrease ? "error" : "success"),
                      amount: loading ? "..." : `${tunggakanPercentageChange.isIncrease ? "+" : "-"}${tunggakanPercentageChange.percentage}%`,
                      label: "vs bulan lepas",
                    }}
                  />
                </MDBox>
              </Grid>

              {/* NOTIS SUMMARY: Below Tunggakan */}
              <Grid item xs={12}>
                <MDBox mt={{ xs: 2, lg: 0 }}>
                  <Projects />
                </MDBox>
              </Grid>
            </Grid>
          </Grid>

          {/* RIGHT COLUMN: Trend Chart */}
          <Grid item xs={12} lg={8}>
            <MDBox pt={{ xs: 3, lg: 0 }} mb={{ xs: 4, lg: 1 }}>
              <ReportsLineChart
                color="info"
                title={isKaunterUser ? "Trend Terimaan (Tahun Fiskal)" : "Trend Tunggakan (Tahun Fiskal)"}
                description={isKaunterUser ? "Jan - Dis tahun semasa" : "Jan - Dis tahun semasa"}
                chart={{
                  labels: rollingMonthYearLabels,
                  datasets: [
                    {
                      label: isKaunterUser ? "Terimaan (RM)" : "Tunggakan (RM)",
                      data: monthlyArrearsData,
                    },
                  ],
                }}
              />
            </MDBox>
          </Grid>

          {/* ACTIVITY WIDGET: Jana Terakhir */}
          <Grid item xs={12}>
            <JanaTerakhir />
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default Dashboard;
